import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Component, Input, PLATFORM_ID, inject, signal } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { ReferAFriendCopy, ReferAFriendDesignMode, ReferAFriendScreen } from '@aceable/shared-ui';
import { DEFAULT_REFER_A_FRIEND_COPY, ReferAFriendComponent, tagReferralLink } from '@aceable/shared-ui';
import type { AmbassadorGetResponse } from '@core-mkt/services/ambassador-get/ambassador-get.service';
import { AmbassadorGetService, REFERRAL_LINK_ERROR } from '@core-mkt/services/ambassador-get/ambassador-get.service';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { MarketingSyncUserService } from '@core-mkt/services/sync-user/marketing-sync-user.service';
import {
  TargetedSyncService,
  normalizePageBrandId,
  linkSignatureMatchesPage,
} from '@core-mkt/services/targeted-sync/targeted-sync.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Value import (not `import type`): it's referenced by @Input() decorator metadata.
import { type CraftCkeditorField, type ReferAFriendGlobalConfig } from './refer-a-friend-container';

/**
 * Thin container that wires the presentational `ReferAFriendComponent` widget (built
 * separately) to the `ambassador/get` integration via `AmbassadorGetService` -- the xcc-api BFF,
 * NOT the vendor directly (EPS-4793). This component owns zero Ambassador API logic of its own,
 * only the screen/loading/error state the widget's Inputs expect.
 *
 * As of EPS-4567 this is a SITE-WIDE global: mounted once per Vision page in
 * `TemplateSingleComponent` and configured from the `referAFriendConfig` aceHeader global
 * (share message + selected channels), not a Craft-authored page block. When that global is
 * absent the container stays fully inert (no trigger delegate, no mint), so the legacy
 * Ambassador (MBSY) `#raf-trigger` flow keeps handling the trigger unchanged.
 *
 * Logged-in users are resolved from `MarketingSyncUserService.syncUserReady$` (the same mechanism
 * `AmbassadorInviteComponent` uses): when the account's brand AND vertical both match this page,
 * the `ambassadorUrl` already on that payload is shown directly. They are NOT auto-minted — see
 * `resolveLoggedInScreen` — so anything unverifiable falls through to the intake screen. Logged-out
 * users see the intake screen and mint a link once they submit an email.
 */
@Component({
  selector: 'refer-a-friend-container',
  templateUrl: './refer-a-friend-container.component.html',
  standalone: true,
  imports: [ReferAFriendComponent],
})
export class ReferAFriendContainerComponent implements OnChanges, OnInit, OnDestroy {
  @Input() config?: ReferAFriendGlobalConfig;

  private readonly ambassadorGet = inject(AmbassadorGetService);
  private readonly syncUserService = inject(MarketingSyncUserService);
  private readonly targetedSync = inject(TargetedSyncService);
  private readonly brandConfig = inject(BrandConfigurationService);
  private readonly envService = inject(EnvService);
  private readonly redactor = inject(WysiwygRedactorService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private readonly destroy$ = new Subject<void>();
  private rafTriggerHandler?: (event: Event) => void;

  // Closed on load: the modal opens when the block's own inline "Get Referral Link" CTA is clicked
  // (onOpen), or when a content-authored #raf-trigger element elsewhere on the page is clicked (the
  // body delegate below) -- the repo's existing referral-trigger convention.
  readonly visible = signal(false);
  // Start on the loading placeholder so the intake form never flashes before we know the user's
  // login/link state; the syncUserReady$ subscription resolves it to 'intake' or 'link'.
  readonly screen = signal<ReferAFriendScreen>('loading');
  readonly referralLink = signal('');
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  // Widget copy. Built once per `config` change (ngOnChanges), NOT on every change-detection tick:
  // `bypassSanitizer` returns a fresh SafeHtml each call, so rebuilding in a getter would hand the OnPush
  // widget a new `copy` reference every tick. The global's Modal Message overrides the intake title + body;
  // everything else stays on DEFAULT_REFER_A_FRIEND_COPY.
  copy: ReferAFriendCopy = DEFAULT_REFER_A_FRIEND_COPY;
  // Channel ids selected in the Craft global (email/facebook/whatsapp/threads/x); null → widget shows all 5.
  selectedChannels: string[] | null = null;
  // Plain-text share message (the global's HTML message, tags stripped) used to build the share-intent hrefs.
  shareMessage = '';

  private lastEmail = '';

  get designMode(): ReferAFriendDesignMode {
    return this.envService.get.brandConfig?.designMode ?? 'ace';
  }

  ngOnChanges(): void {
    this.buildViewModel();
  }

  /**
   * Maps the `referAFriendConfig` global into the widget's inputs:
   * - `referAFriendModalMessage` — the modal's on-screen copy. A flattened CKEditor string; run through
   *   `bypassSanitizer` → SafeHtml and used to REPLACE the intake title + body (the widget renders `body`
   *   via `[innerHTML]`, and the title is blanked so only the authored message shows). Everything else on
   *   `copy` keeps DEFAULT_REFER_A_FRIEND_COPY (share heading, form-config labels).
   * - `referAFriendMessage` — the share-intent text. Also a `{ html }` field (flattened to a string), only
   *   tags-stripped to plain text for the share hrefs (not rendered as visible copy).
   * - `referAFriendSocialOptions` — the id list the widget uses to pick which channels show.
   */
  private buildViewModel(): void {
    const modalMessage = this.richOrNull(this.config?.referAFriendModalMessage);
    this.copy = modalMessage
      ? { ...DEFAULT_REFER_A_FRIEND_COPY, title: '', body: modalMessage }
      : DEFAULT_REFER_A_FRIEND_COPY;
    // `referAFriendMessage` is a `{ html }` field too, so post-flatten it's a string (or a residual
    // `{ html: null }`); normalize to text before stripping tags for the share-intent hrefs.
    this.shareMessage = this.stripHtml(this.asText(this.config?.referAFriendMessage));
    this.selectedChannels = this.config?.referAFriendSocialOptions ?? null;
  }

  /**
   * A populated CKEditor field arrives post-flatten as a non-empty string; an empty one is absent or a
   * residual `{ html: null }` object (flatten only collapses `{ html: <string> }`). Return trusted SafeHtml
   * for the former, else null so the caller falls back to the default copy.
   */
  private richOrNull(value: string | CraftCkeditorField | null | undefined): SafeHtml | null {
    return typeof value === 'string' && value.length > 0 ? this.redactor.bypassSanitizer(value) : null;
  }

  /** Post-flatten a populated CKEditor field is a string; an empty one is a residual `{ html: null }`. */
  private asText(value: string | CraftCkeditorField | null | undefined): string {
    return typeof value === 'string' ? value : '';
  }

  /**
   * The site-wide widget must stay inert unless the aceHeader global is actually configured. "Configured"
   * = ANY of the three fields populated: the modal message (a non-empty flattened string), the share
   * message, or a non-empty social-options selection. A page with only social options (default modal copy)
   * or only a modal message must still activate.
   */
  private isConfigured(): boolean {
    const config = this.config;
    if (!config) {
      return false;
    }
    const hasModalMessage =
      typeof config.referAFriendModalMessage === 'string' && config.referAFriendModalMessage.length > 0;
    const hasShareMessage = typeof config.referAFriendMessage === 'string' && config.referAFriendMessage.length > 0;
    const hasSocialOptions = !!config.referAFriendSocialOptions?.length;
    return hasModalMessage || hasShareMessage || hasSocialOptions;
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  ngOnInit(): void {
    // Safety net for the case where `config` is set without a bound-input change (no ngOnChanges):
    // the view-model is idempotent, so a rebuild here is harmless.
    this.buildViewModel();

    // FALLBACK GATE (EPS-4567): this widget is now mounted on EVERY Vision page. It must do nothing
    // unless the aceHeader `referAFriendConfig` global is actually configured (any of modal message,
    // share message, or social options). When it isn't, we skip both the trigger delegate and the mint
    // subscription, so a page's bare `#raf-trigger` id is left entirely to the legacy Ambassador (MBSY)
    // delegate in the Gladly `referral-popup` component (referral-popup.component.ts). (ambassador-invite's
    // own MBSY delegate was removed once its buttons moved to the `.raf-trigger` class — EPS-4309.)
    if (!this.isConfigured()) {
      return;
    }

    // A mint has no SSR value -- running it during server rendering would enrol a GetAmbassador
    // contact on every page render (including crawlers) instead of once per real visitor, so the
    // mint-on-login-detected path is browser-only. Still true after EPS-4793 routed the call through
    // xcc-api: the BFF forwards to the same third-party, so SSR would enrol just as eagerly.
    // This is a platform check, not the reference component's `ambassadorIntegrationId` brand gate:
    // that id is unrelated to this container's mint, and gating on it would break brands (e.g. aa,
    // ids) that have no `ambassadorIntegrationId` configured.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // The ONLY way this widget opens: a body-level delegate on the CKEditor "Refer a Friend" button,
    // which carries the `.raf-trigger` class (ecom-craftcms-v3 EPS-4569). Also matches an anchor whose
    // href ends in `#raf-trigger` (an authored stand-alone-cta/anchor-button emits href, not the class).
    // Deliberately does NOT match the bare `#raf-trigger` id, which stays exclusive to the legacy MBSY
    // delegate (ambassador-invite.component.ts) so the two never double-fire. preventDefault stops an
    // anchor from hash-jumping.
    this.rafTriggerHandler = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('.raf-trigger, a[href$="#raf-trigger"]')) {
        event.preventDefault();
        this.onOpen();
      }
    };
    this.document.body.addEventListener('click', this.rafTriggerHandler, false);

    this.syncUserService.syncUserReady$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      // Not logged in -> resolve the loading placeholder to the email-intake screen.
      if (!response?.success || !response.data?.isAuthenticated) {
        this.screen.set('intake');
        return;
      }

      // Brand gate (EPS-4664): the sync-marketing link is account-level and brand-blind, so on its
      // own it surfaces a user's link on a brand they don't belong to (e.g. an IDS ambassador on the
      // ACE page). Only resolve a logged-in link when the account brand matches THIS page's brand;
      // otherwise route to intake so the user signs up for the brand they're actually viewing.
      this.resolveLoggedInScreen(response.data.ambassadorUrl);
    });
  }

  /**
   * Brand-gates the logged-in link resolution. Mirrors `AmbassadorInviteComponent`'s
   * `verifyBrandAndSetLoggedInLink`, and requires BOTH halves to pass:
   *
   * - BRAND — `targeted-sync.brandId` vs the page's `iterable.brandId`. Separates ACE/IDS/DEC/AARP.
   * - VERTICAL — the link's OWN host + `utm_campaign` tokens vs this page's signature. Separates the
   *   verticals within ACE, which all report `brandId: ACE` (Aceable, AceableAgent, INS, MTG, HSP).
   *
   * Neither is sufficient alone: brand-only renders an AceableAgent link on Aceable, vertical-only
   * renders an I Drive Safely link on Aceable (both are driving). Anything else — no page brand, an
   * unrecognised link, either mismatch, or a request error — FAILS CLOSED to the intake screen.
   * Applies to every brand (no `ambassadorIntegrationId` gate).
   */
  private resolveLoggedInScreen(ambassadorUrl: string | undefined): void {
    const pageBrandId = normalizePageBrandId(this.brandConfig.config?.iterable?.brandId);
    if (!pageBrandId) {
      this.screen.set('intake');
      return;
    }

    // Vertical half of the gate: does this link identify itself as belonging on THIS page? Derived
    // from the URL, so it also covers the missing-url case (parse fails → intake).
    if (!linkSignatureMatchesPage(ambassadorUrl, this.brandConfig.config?.iterable)) {
      this.screen.set('intake');
      return;
    }

    this.targetedSync
      .getTargetedSyncUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (targeted) => {
          const accountBrandId = (targeted?.brandId ?? '').toUpperCase();
          if (!accountBrandId || accountBrandId !== pageBrandId) {
            this.screen.set('intake');
            return;
          }

          // Brand and vertical both match. Use the resolved sync-marketing link directly (no browser
          // call to the GetAmbassador API, which is unnecessary here and blocked by CORS off
          // deployed origins).
          // The url is already known non-empty (the signature check above cannot pass without
          // parsing one); the guard below is kept as defence in depth.
          // Do NOT fall back to minting when it is absent. xcc-api picks a minted link by
          // `campaign_uid` only for brands with a mapped campaign; every other vertical gets the first
          // link that isn't a mapped brand's, which can still belong to another unmapped vertical —
          // the exact leak this gate exists to stop. Route to intake instead (EPS-4664).
          if (ambassadorUrl) {
            this.referralLink.set(this.withInviteCampaign(ambassadorUrl));
            this.screen.set('link');
          } else {
            this.screen.set('intake');
          }
        },
        error: () => {
          this.screen.set('intake');
        },
      });
  }

  ngOnDestroy(): void {
    if (this.rafTriggerHandler) {
      this.document.body.removeEventListener('click', this.rafTriggerHandler, false);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmitEmail(email: string): void {
    this.mintLink(email);
  }

  onRetry(): void {
    if (this.lastEmail) {
      this.mintLink(this.lastEmail);
    }
  }

  onCopyLink(): void {
    // The widget already copies to the clipboard itself; nothing for the container to do.
    // Output is still wired so a future analytics hook can listen without touching the widget.
  }

  onOpen(): void {
    // Opening is idempotent, so a stray double-click (or two matching #raf-trigger elements on the page) is
    // harmless. The syncUserReady$ subscription in ngOnInit has usually already resolved `screen` by now;
    // the 'loading' placeholder covers the rare not-yet-resolved case.
    this.visible.set(true);
  }

  onCloseModal(): void {
    // The widget has no visibility into what mounted it, so hiding on close is this container's
    // job. Re-opening is handled by the #raf-trigger CTA delegate, so close is not one-way.
    this.visible.set(false);
  }

  private mintLink(email: string): void {
    this.lastEmail = email;
    this.loading.set(true);
    this.error.set(null);
    // Retry from the 'link' screen (the logged-in failure surface) would otherwise flash an empty
    // link-pill — the widget renders the pill on `screen==='link' && !error`, and we just cleared
    // `error`. Drop back to the loading placeholder so an in-flight retry shows the spinner instead.
    // The logged-out 'intake' path is left as-is so its form stays visible (its disabled CTA is the
    // in-flight indicator).
    if (this.screen() === 'link') {
      this.screen.set('loading');
    }
    // Routed through xcc-api, which resolves the GetAmbassador account AND group from the `brand`
    // param in one place (EPS-4739). Going through the BFF also keeps the vendor API key out of the
    // browser bundle and stops IDS minting onto the Aceable account (EPS-4793).
    //
    // THIS surface never opts into `email_new_ambassador` -- note the single argument. The widget
    // shows the minted link inline, and a logged-out visitor can type any address, so mailing it
    // would be a spam/abuse vector (EPS-4531). An earlier version of this comment said the ROUTE
    // never sends the flag; that is no longer true -- the invite modal and the Gladly popup opt in,
    // and xcc-api gates their request on brand + Origin. The distinction matters: the protection
    // here is this call site not asking, not the route refusing.
    // No takeUntil, matching master: this subscription ran to completion there, and EPS-4793 is a
    // re-route rather than a behaviour change. (The popup keeps its own -- it already had one.)
    this.ambassadorGet.getReferralLink(email).subscribe({
      // Read defensively rather than destructuring: an exception thrown inside `next` is routed by
      // rxjs to reportUnhandledError, NOT to the `error` handler below, so a null/absent body would
      // strand the widget on the loading spinner with no error and no Retry. This also keeps the
      // `!url?.trim()` guard below load-bearing rather than an optional chain on a non-nullish type.
      next: (response: AmbassadorGetResponse | null) => {
        const url = response?.url;
        // A 200 with a missing/blank link is as unusable as an HTTP failure -- route it to the same
        // error/retry state rather than flipping to the share screen with an empty pill.
        //
        // Deliberately NOT signature-gated, unlike `resolveLoggedInScreen`. A minted link is not
        // guaranteed to carry the vanity host + `utm_campaign` that `linkSignatureMatchesPage` keys
        // on -- that table was built from `sync-marketing` urls, and `ambassador/get` has returned an
        // unbranded `blue.mbsy.co` host. Gating on it rejected valid ACE and DEC links outright.
        //
        // The mint is scoped server-side instead: xcc-api resolves the account and group from the
        // `brand` param (`AmbassadorApiService.getCredentials`), and the campaign as well for brands
        // with a mapped campaign. `sync-marketing` returns one account-level url with no brand
        // scoping at all. That asymmetry is why the logged-in gate stays and this one does not.
        // See the matching note in `ambassador-invite.component.ts`.
        if (!url?.trim()) {
          this.failMint();
          return;
        }
        this.referralLink.set(this.withInviteCampaign(url));
        this.error.set(null);
        this.loading.set(false);
        this.screen.set('link');
      },
      error: () => {
        this.failMint();
      },
    });
  }

  /**
   * Tags the shown link with this brand's invite-page campaign. Applied only to the link handed to the
   * widget, after the logged-in signature gate has read GetAmbassador's original `utm_campaign`, so the
   * gate keeps matching on the vendor's value. Keyed on `brandConfig.id`, the same id the mint sends.
   */
  private withInviteCampaign(url: string): string {
    return tagReferralLink(url, this.brandConfig.id, 'Invite');
  }

  /**
   * Shared failure handling for a mint that errored or returned no usable link. Shows a generic
   * message (never the raw error, which would leak the requested URL and the visitor's email) and, if the
   * initial logged-in mint was still on the loading placeholder, advances to the 'link' screen so the
   * user gets the error + a Retry button (the widget renders that error/retry block only on 'link';
   * with `error` set it suppresses the empty pill). Retry re-mints from `lastEmail` — the account
   * email the logged-in path already captured — so the user never has to type an email they bypassed.
   * The logged-out submit path is already on 'intake' (its own form CTA is the retry), so its
   * behaviour is unchanged.
   */
  private failMint(): void {
    this.loading.set(false);
    if (this.screen() === 'loading') {
      this.screen.set('link');
    }
    this.error.set(REFERRAL_LINK_ERROR);
  }
}
